"use client";

import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

type RevealProps = {
  children?: React.ReactNode;
  className?: string;
  /** Délai d'apparition (s) */
  delay?: number;
  /** Accepté pour compatibilité d'API, sans effet (apparition opacity-only). */
  y?: number;
};

/**
 * Apparition au scroll — fondu en opacité uniquement (pas de transform, pour
 * éviter la rasterisation des sections lourdes qui saccadait le scroll).
 * Déclenché une seule fois par un IntersectionObserver.
 */
export function Reveal({ delay = 0, children, className }: RevealProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold: 0.12 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={cn("fade-in", visible && "is-in", className)}
      style={{ "--fade-delay": `${delay}s` } as React.CSSProperties}
    >
      {children}
    </div>
  );
}
